import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { touchSharedSessionActivity } from "@/lib/sessionIdle";
import { useDespachoPanelVisible } from "@/modules/despacho/useDespachoPanelVisible";
import { operationalReportsApi } from "@/modules/despacho/operationalReportsApi";
import { buildDespachoTeamActivityFetchParams } from "@/modules/despacho/despachoTeamActivity";
import { mockRecordatoriosDespacho } from "@/modules/despacho/despachoMockData";

const EMPTY_SUMMARY = {
  alertsCount: 0,
  clientMessagesCount: 0,
  teamChatHintCount: 0,
  teamChatGroupCount: 0,
  teamChatPrivateCount: 0,
  latestPrivateSenderId: "",
  onlineCount: 0,
  presenceOk: false,
  despachoActive: false,
  alertItems: [],
  personalNoteAlerts: [],
  personalNotesDueCount: 0,
  assignedTasksPendingCount: 0,
  assignedTasksBannerCount: 0,
  assignedTasksUnreadCount: 0,
};

function sumKpi(kpis, keys) {
  if (!kpis || typeof kpis !== "object") return 0;
  return keys.reduce((sum, key) => sum + Number(kpis[key] || 0), 0);
}

/**
 * Resumen ligero para badges del BNT Asistente (sin tocar SAT/XML).
 */
export function useBntAsistenteSummary({ enabled = true, pollMs = 60000, keepaliveSession = false } = {}) {
  const { user } = useAuth();
  const despachoActive = useDespachoPanelVisible();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(Boolean(enabled && user));

  const reload = useCallback(async () => {
    if (!enabled || !user) {
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [presenceRes, chatRes, notesRes, tasksRes] = await Promise.all([
        api.get("/workspace/chat/presence", { timeout: 15000 }).catch(() => ({ data: {} })),
        api.get("/workspace/chat/messages", {
          params: buildDespachoTeamActivityFetchParams(),
          timeout: 15000,
        }).catch(() => ({ data: {} })),
        api.get("/asistente/personal-notes/due-alerts", { timeout: 15000 }).catch(() => ({ data: {} })),
        api.get("/asistente/assigned-tasks/pending-alerts", { timeout: 15000 }).catch(() => ({ data: {} })),
      ]);

      const presenceItems = presenceRes.data?.items || [];
      const onlineCount = Number(
        presenceRes.data?.online_count
        ?? presenceItems.filter((row) => row?.is_online).length,
      );
      const messages = chatRes.data?.items || [];
      const othersMessages = messages.filter((m) => m?.sender_user_id && m.sender_user_id !== user.id);
      const groupMessages = othersMessages.filter((m) => String(m?.channel || "group") !== "direct");
      const privateMessages = othersMessages.filter((m) => String(m?.channel || "") === "direct");
      const teamChatGroupCount = Math.min(groupMessages.length, 99);
      const teamChatPrivateCount = Math.min(privateMessages.length, 99);
      const teamChatHintCount = teamChatGroupCount + teamChatPrivateCount;
      const latestPrivateMessage = privateMessages[privateMessages.length - 1];
      const latestPrivateSenderId = latestPrivateMessage?.sender_user_id || "";

      const personalDueRaw = Array.isArray(notesRes.data?.items) ? notesRes.data.items : [];
      const personalNoteAlerts = personalDueRaw.slice(0, 12).map((row) => ({
        id: `note-${row.id}`,
        title: row.body,
        subtitle: row.due_date ? `Fecha ${String(row.due_date).slice(0, 10)}` : "Mis notas",
        prioridad: row.priority === "alta" ? "Alta" : row.priority === "baja" ? "Baja" : "Media",
        priority: row.priority,
        kind: "personal_note",
      }));
      const personalNotesDueCount = personalNoteAlerts.length;

      const assignedTasksPending = Array.isArray(tasksRes.data?.items) ? tasksRes.data.items : [];
      const assignedTasksPendingCount = Number(tasksRes.data?.count || assignedTasksPending.length || 0);
      const assignedTasksBannerCount = assignedTasksPendingCount;
      const assignedTasksUnreadCount = Number(
        tasksRes.data?.unread_count
        ?? (Array.isArray(tasksRes.data?.unread_items) ? tasksRes.data.unread_items.length : 0),
      );

      let alertsCount = mockRecordatoriosDespacho.length;
      let clientMessagesCount = 0;
      let alertItems = mockRecordatoriosDespacho.slice(0, 8).map((row) => ({
        id: row.id || row.tipo,
        title: row.tipo,
        subtitle: row.cliente || "",
        prioridad: row.prioridad || "Media",
        href: "/despacho/tareas",
      }));

      if (despachoActive) {
        try {
          const dash = await operationalReportsApi.getDashboard();
          const kpis = dash?.kpis || {};
          const openAlerts = sumKpi(kpis, ["open_alerts"]);
          const overdue = sumKpi(kpis, ["overdue_instances"]);
          const dueWeek = sumKpi(kpis, ["due_this_week"]);
          const pendingTasks = sumKpi(kpis, ["pending_tasks"]);
          clientMessagesCount = sumKpi(kpis, ["client_messages_pending"]);
          alertsCount = openAlerts + overdue + dueWeek + pendingTasks;
          if (alertsCount > 0) {
            alertItems = [
              overdue > 0 ? { id: "overdue", title: "Obligaciones vencidas", subtitle: `${overdue} instancia(s)`, prioridad: "Crítico", href: "/despacho/reportes?report=overdue" } : null,
              dueWeek > 0 ? { id: "due_week", title: "Vencen esta semana", subtitle: `${dueWeek} instancia(s)`, prioridad: "Alta", href: "/despacho/reportes?report=upcoming_due_dates" } : null,
              pendingTasks > 0 ? { id: "tasks", title: "Tareas pendientes", subtitle: `${pendingTasks} tarea(s)`, prioridad: "Media", href: "/despacho/tareas" } : null,
              openAlerts > 0 ? { id: "alerts", title: "Alertas abiertas", subtitle: `${openAlerts} alerta(s)`, prioridad: "Urgente", href: "/despacho/calendario?tab=auto-alertas&sub=tablero" } : null,
            ].filter(Boolean);
          } else {
            alertItems = [];
          }
        } catch {
          // conservar mock si el dashboard no responde
        }
      }

      setSummary({
        alertsCount,
        clientMessagesCount,
        teamChatHintCount,
        teamChatGroupCount,
        teamChatPrivateCount,
        latestPrivateSenderId,
        onlineCount,
        presenceOk: onlineCount > 0,
        despachoActive,
        alertItems,
        personalNoteAlerts,
        personalNotesDueCount,
        assignedTasksPendingCount,
        assignedTasksBannerCount,
        assignedTasksUnreadCount,
      });
      if (keepaliveSession) touchSharedSessionActivity();
    } catch {
      setSummary({ ...EMPTY_SUMMARY, despachoActive });
    } finally {
      setLoading(false);
    }
  }, [enabled, user, despachoActive, keepaliveSession]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!enabled || !user || !pollMs) return undefined;
    const id = window.setInterval(reload, pollMs);
    return () => window.clearInterval(id);
  }, [enabled, user, pollMs, reload]);

  return { summary, loading, reload };
}
